import { axiosInstance } from "@/axios/interceptors";
import { ENV } from "@/axios/constants";
import { ZodSentryReporter } from "@/lib/zod-sentry-reporter";
import { z } from "zod";

export const CreditCardTypesSchema = z.union([
	z.literal("MASTERCARD"),
	z.literal("VISA"),
	z.literal("DISCOVER"),
	z.literal("AMEX"),
	z.literal("OTHER"),
]);
export type CreditCardTypes = z.infer<typeof CreditCardTypesSchema>;

export const CreditCardSchema = z.object({
	id: z.number(),
	location_id: z.number(),
	reservation_id: z.number().nullable(),
	cc_token: z.string(),
	card_type: CreditCardTypesSchema,
	card_holder: z.string().nullable(),
	last4: z.string(),
	exp_month: z.string(),
	exp_year: z.string(),
	cvv: z.string().nullable(),
	is_disabled: z.union([z.literal(0), z.literal(1)]),
	default: z.preprocess(
		(val) => (val === true ? 1 : val === false ? 0 : val),
		z.union([z.literal(0), z.literal(1)]),
	),
	created_datetime: z.string().optional(),
});

export const CreditCardListSchema = z.array(CreditCardSchema);
export type CreditCard = z.infer<typeof CreditCardSchema>;
export type CreditCardList = z.infer<typeof CreditCardListSchema>;

export type GetGuestCreditCardsParams = {
	guestId: number;
	locationId: number;
};

export async function getGuestCreditCards({
	guestId,
	locationId,
}: GetGuestCreditCardsParams): Promise<CreditCardList> {
	const userId = ENV.VITE_USER_ID;
	const response = await axiosInstance.get(
		`/locations/${locationId}/guests/${guestId}/guest_cc_token`,
		{
			params: {
				guest_id: guestId,
				user_id: userId,
			},
		},
	);
	return ZodSentryReporter.parse(CreditCardListSchema, response);
}

export type AddGuestCreditCardParams = {
	guestId: number;
	locationId: number;
	cc_token: string;
	card_type: string;
	card_holder: string;
	last4: string;
	exp_month: string;
	exp_year: string;
	transaction_no: string;
	cvv?: string;
	default?: boolean;
	reusable?: number;
	flow?: string;
	billing_address?: {
		street: string;
		street_2: string;
		zip: string;
		city: string;
		state: string;
		country_iso: string;
		email: string;
	};
};

export async function addGuestCreditCard({
	guestId,
	locationId,
	cc_token,
	card_type,
	card_holder,
	last4,
	exp_month,
	transaction_no,
	exp_year,
	cvv = "",
	default: isDefault = false,
	reusable = 1,
	flow = "profile",
	billing_address,
}: AddGuestCreditCardParams) {
	const userId = ENV.VITE_USER_ID;
	const response = await axiosInstance.post(
		`/locations/${locationId}/guest_cc_token`,
		{
			cc_token,
			card_type,
			card_holder,
			last4,
			exp_month,
			exp_year,
			transaction_no,
			cvv,
			default: isDefault,
			reusable,
			flow,
			guest_id: guestId,
			user_id: userId,
			billing_address,
		},
	);

	const parsed = ZodSentryReporter.safeParse(CreditCardSchema, response);
	return parsed.success ? parsed.data : response.data;
}

export type DeleteGuestCreditCardParams = {
	guestId: number;
	creditCardId: number;
	locationId: number;
};

export async function deleteGuestCreditCard({
	guestId,
	creditCardId,
	locationId,
}: DeleteGuestCreditCardParams) {
	const userId = ENV.VITE_USER_ID;

	const response = await axiosInstance.delete(
		`/locations/${locationId}/guests/${guestId}/guest_cc_token/${creditCardId}`,
		{
			params: {
				user_id: userId,
			},
		},
	);
	return response.data;
}

export type EditCreditCardPayload = {
	guestId: number;
	creditCardId: number;
	locationId: number;
	exp_month: string;
	exp_year: string;
	default: number;
	user_id: number;
};

export const EditCreditCardResponseSchema = z.object({
	exp_month: z.string(),
	exp_year: z.string(),
	default: z.number(),
});

export type EditCreditCardResponse = z.infer<
	typeof EditCreditCardResponseSchema
>;

export async function editCreditCard({
	guestId,
	creditCardId,
	locationId,
	exp_month,
	exp_year,
	default: isDefault,
	user_id,
}: EditCreditCardPayload) {
	const response = await axiosInstance.put(
		`/locations/${locationId}/guests/${guestId}/guest_cc_token/${creditCardId}/default`,
		{
			exp_month,
			exp_year,
			default: isDefault,
			user_id,
		},
	);
	return ZodSentryReporter.parse(EditCreditCardResponseSchema, response);
}
